import React from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const emptyPkg = { name: "", code: "", description: "", items: [], daily_price: 0, min_duration: 1, discount: 0, active: true };

export default function PackagesPage() {
  const [rows, setRows] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(emptyPkg);
  const [editId, setEditId] = React.useState(null);

  const load = React.useCallback(async () => {
    const [p, pr] = await Promise.all([api.get("/packages"), api.get("/products")]);
    setRows(p.data); setProducts(pr.data);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", qty: 1 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, patch) => {
    const items = [...form.items];
    items[i] = { ...items[i], ...patch };
    setForm({ ...form, items });
  };

  const save = async () => {
    try {
      if (editId) await api.patch(`/packages/${editId}`, form);
      else await api.post("/packages", form);
      toast.success("Tersimpan"); setOpen(false); setForm(emptyPkg); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (p) => {
    setForm({
      name: p.name, code: p.code || "", description: p.description || "",
      items: (p.items || []).map((it) => ({ product_id: it.product_id, qty: it.qty || 1 })),
      daily_price: p.daily_price || 0, min_duration: p.min_duration || 1,
      discount: p.discount || 0, active: p.active !== false,
    });
    setEditId(p.id); setOpen(true);
  };

  const productName = (id) => products.find((p) => p.id === id)?.name || "-";

  return (
    <div className="space-y-4" data-testid="packages-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Paket Equipment</h1>
          <p className="text-sm text-neutral-500">Kelola bundle equipment produksi.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyPkg); setEditId(null); } }}>
          <DialogTrigger asChild><Button className="bg-green-600 hover:bg-green-700" data-testid="btn-new-package"><Plus className="h-4 w-4 mr-1" />Paket Baru</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editId ? "Edit Paket" : "Paket Baru"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pkg-name" /></div>
                <div><Label>Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
                <div><Label>Harga Paket/hari</Label><Input type="number" value={form.daily_price} onChange={(e) => setForm({ ...form, daily_price: +e.target.value })} data-testid="pkg-price" /></div>
                <div><Label>Min Duration (hari)</Label><Input type="number" value={form.min_duration} onChange={(e) => setForm({ ...form, min_duration: +e.target.value })} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1"><Label>Isi Paket</Label><Button variant="outline" size="sm" onClick={addItem} data-testid="pkg-add-item"><Plus className="h-3 w-3" /></Button></div>
                {form.items.map((it, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <select className="flex-1 border border-neutral-200 rounded px-2 py-1 text-sm" value={it.product_id} onChange={(e) => updateItem(i, { product_id: e.target.value })}>
                      <option value="">— pilih —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <Input type="number" min={1} value={it.qty} onChange={(e) => updateItem(i, { qty: +e.target.value })} className="w-20" />
                    <button onClick={() => removeItem(i)}><Trash2 className="h-4 w-4 text-red-500" /></button>
                  </div>
                ))}
              </div>
              <div><Label>Deskripsi</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-package">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((p) => (
          <div key={p.id} className="card-flat p-4 relative" data-testid={`package-card-${p.id}`}>
            <button onClick={() => openEdit(p)} className="absolute top-3 right-3 text-xs text-green-700 hover:underline flex items-center gap-1" data-testid={`edit-package-${p.id}`}>
              <Pencil className="h-3 w-3" /> Edit
            </button>
            <div className="text-xs font-mono text-neutral-500">{p.code}</div>
            <div className="font-semibold text-lg pr-14">{p.name}</div>
            <div className="text-xs text-neutral-500 mt-1 mb-2">{p.description}</div>
            <div className="text-xs space-y-0.5 mt-2">
              {p.items?.map((it, i) => (
                <div key={i}>• {productName(it.product_id)} × {it.qty}</div>
              ))}
            </div>
            <div className="border-t border-neutral-200 mt-3 pt-2 text-green-700 font-semibold">{formatIDR(p.daily_price)}/hari</div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-neutral-500 col-span-3">Belum ada paket.</div>}
      </div>
    </div>
  );
}
